import { useState, useEffect } from 'react';
import api from '../api';

// Owns the current user's profile + household server state and mutations.
export default function useProfile(token, setToken, onExternalUpdate) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get('/user/me');
      setUserData(res.data);
    } catch (err) {
      console.error("Error refreshing profile", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (householdName, householdCode) => {
    if (!householdName || !householdCode) {
      alert("Please provide both a name and a PIN.");
      return;
    }

    try {
      const res = await api.post('/household', { name: householdName, joinCode: householdCode });
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);
      alert(`Household "${res.data.household.name}" created!`);
      await refreshProfile();
    } catch (err) {
      alert(err.response?.data?.error || "Error creating household");
    }
  };

  const handleJoin = async (inviteCode, joinCode) => {
    try {
      const res = await api.post('/household/join', { inviteCode, joinCode });
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);
      await refreshProfile();
    } catch (err) {
      alert(err.response?.data?.error);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm("Leave this household?")) return;
    try {
      const res = await api.post('/household/leave', {});
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);
      await refreshProfile();
    } catch (err) {
      alert("Error leaving");
    }
  };

  const handleResetPin = async (newPin, setIsResetting, setNewPin) => {
    try {
      await api.put('/household/reset-pin', { newPin });
      alert("PIN has been reset! Make sure to tell your roommates.");
      setIsResetting(false);
      setNewPin("");
    } catch (err) {
      alert("Error resetting PIN.");
    }
  };

  const handleUpdateProfile = async (data) => {
    try {
      await api.patch('/user/me', data);
      await refreshProfile();
      onExternalUpdate?.(); // Triggers global refresh in App.js
    } catch (err) {
      alert("Failed to update profile");
    }
  };

  const handleUpdateHousehold = async (data) => {
    try {
      await api.patch('/household', data);
      await refreshProfile();
      onExternalUpdate?.();
    } catch (err) {
      alert("Failed to update household");
    }
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/upload/image', formData);
      return res.data.imageUrl;
    } catch (err) {
      console.error("Image upload error", err);
      throw err;
    }
  };

  return {
    userData, loading,
    handleCreate, handleJoin, handleLeave, handleResetPin,
    handleUpdateProfile, handleUpdateHousehold, handleImageUpload,
  };
}
